import { useMemo } from "react";
import { Button, NavItem, useNavigationFilter } from "@components";
import { PaperAirplaneIcon } from "@heroicons/react/24/outline";
import { cn } from "@utils";
import { buildNavigation } from "../navigationConfig";
import { SidebarFooter } from "./SidebarFooter";
import { useCustomerGroups } from "@context/CustomerGroupsContext";
import { useRequestTemplates, useQueries, useAiFeatures } from "@flags";
import { useMerchant } from "@auth";
import { useQuery } from "@tanstack/react-query";
import { fetchQueries } from "@api";
import { todaysTasksQueryOptions } from "@areas/tasks/hooks/useTasks";

export function SidebarNav({
  collapsed = false,
  onNavigate,
  onRequestPayment,
  onAction,
}) {
  const { filterNavigation } = useNavigationFilter();
  const { groups: customerGroups, customersCount } = useCustomerGroups();
  const requestTemplatesEnabled = useRequestTemplates();
  const aiEnabled = useAiFeatures();
  const queriesEnabled = useQueries();
  const { branchScopingEnabled } = useMerchant();

  const { data: openQueriesData } = useQuery({
    queryKey: ["queries", "open-count"],
    queryFn: ({ signal }) => fetchQueries("status=UnderReview&pageSize=1", { signal }),
    staleTime: 60_000,
    enabled: queriesEnabled,
  });
  const openQueriesCount = openQueriesData?.rows ?? 0;

  // Reuse the Tasks page's own query (shared cache) and select just the outstanding count — one
  // fetch serves both, and completing/dismissing a task (which refetches this key) updates the
  // badge automatically. A 60s background poll keeps the badge current while the user works
  // elsewhere in the app — most tasks are backend-generated (missed payments), so there's no
  // user action to react to; the poll pauses when the tab is unfocused (RQ default) and
  // refetch-on-focus catches up on return. Only fetched when the feature is on.
  const { data: todaysTasksCount = 0 } = useQuery({
    ...todaysTasksQueryOptions,
    enabled: aiEnabled,
    refetchInterval: 60_000,
    select: (data) => data.outstandingCount ?? 0,
  });

  const navigation = useMemo(
    () =>
      buildNavigation(
        branchScopingEnabled
          ? customerGroups.map((g) => ({ ...g, membershipCount: undefined }))
          : customerGroups,
        {
          customersCount: branchScopingEnabled ? null : customersCount,
          requestTemplatesEnabled,
          openQueriesCount,
          calendarEnabled: aiEnabled,
          queriesEnabled,
          todaysTasksEnabled: aiEnabled,
          todaysTasksCount,
        },
      ),
    [
      customerGroups,
      customersCount,
      requestTemplatesEnabled,
      openQueriesCount,
      aiEnabled,
      queriesEnabled,
      todaysTasksCount,
      branchScopingEnabled,
    ],
  );

  return (
    <nav className="flex flex-1 flex-col">
      <div className="border-b border-zinc-200 py-4 h-16 px-2">
        <Button
          className="w-full justify-start overflow-hidden whitespace-nowrap"
          color="secondary"
          onClick={onRequestPayment}
          title={collapsed ? "Request a payment" : undefined}
        >
          <PaperAirplaneIcon
            aria-hidden="true"
            className="shrink-0 inline size-4 mr-2"
          />
          <span>Request a payment</span>
        </Button>
      </div>

      <ul role="list" className="space-y-px px-2 py-3">
        {filterNavigation(navigation).map((item) => (
          <li key={item.name}>
            <NavItem
              item={item}
              collapsed={collapsed}
              onNavigate={onNavigate}
              onAction={onAction}
            />
          </li>
        ))}
      </ul>

      <SidebarFooter collapsed={collapsed} onNavigate={onNavigate} />
    </nav>
  );
}
